import { ArrowRight, MapPin } from "lucide-react";
import { Link } from "wouter";
import { trackSectionCtaClick } from "@/lib/analytics";

export type LocalIndustryContext = "general" | "visibility" | "growth" | "aeo" | "paid" | "website";

type LocalIndustryGuide = {
  href: string;
  title: string;
  description: string;
};

const GUIDES: Record<LocalIndustryContext, LocalIndustryGuide[]> = {
  general: [
    {
      href: "/local/cafe-marketing-richmond-putney-wimbledon/",
      title: "Café marketing in Richmond, Putney & Wimbledon",
      description: "A practical local-discovery page for hospitality businesses building visibility across search and maps.",
    },
    {
      href: "/local/restaurant-marketing-richmond-kingston-guildford/",
      title: "Restaurant marketing in Richmond, Kingston & Guildford",
      description: "Explore local search, content and campaign considerations for restaurant demand generation.",
    },
    {
      href: "/local/gym-marketing-putney-wimbledon-kingston/",
      title: "Gym marketing in Putney, Wimbledon & Kingston",
      description: "A sector-specific route for fitness businesses competing for local discovery and enquiries.",
    },
  ],
  visibility: [
    {
      href: "/local/cafe-marketing-richmond-putney-wimbledon/",
      title: "Café local visibility guide",
      description: "See the visibility questions hospitality businesses should answer for location-led search.",
    },
    {
      href: "/local/clinic-medical-dental-marketing-sw-london/",
      title: "Clinic and dental local visibility guide",
      description: "Understand the discovery and trust signals that matter for health and wellbeing services.",
    },
    {
      href: "/local/salon-beauty-marketing-sw-london-surrey/",
      title: "Salon and beauty local visibility guide",
      description: "A practical route for appointment-led businesses across SW London and Surrey.",
    },
  ],
  growth: [
    {
      href: "/local/gym-marketing-putney-wimbledon-kingston/",
      title: "Gym growth guide",
      description: "Learn how a local growth programme can connect discovery, paid demand and lead tracking.",
    },
    {
      href: "/local/restaurant-marketing-richmond-kingston-guildford/",
      title: "Restaurant growth guide",
      description: "Explore a local demand-generation route for restaurant operators and multi-site teams.",
    },
    {
      href: "/local/tyre-mot-garage-marketing-sw-london-surrey/",
      title: "Garage and MOT growth guide",
      description: "A specialist route for high-intent local search and measurable service enquiries.",
    },
    {
      href: "/local/tanning-salon-marketing-sw-london-surrey/",
      title: "Tanning salon growth guide",
      description: "A sector-specific route for booking-led local discovery, seasonal demand and measurement.",
    },
  ],
  aeo: [
    {
      href: "/local/clinic-medical-dental-marketing-sw-london/",
      title: "Clinic and dental AI-search guide",
      description: "Review how clear services, source-qualified content and local entities support discoverability.",
    },
    {
      href: "/local/law-firm-solicitor-marketing-sw-london-surrey/",
      title: "Law firm and solicitor AI-search guide",
      description: "A sector route for professional services that need credible, answer-ready local information.",
    },
    {
      href: "/local/pilates-studio-marketing-sw-london-surrey/",
      title: "Pilates studio AI-search guide",
      description: "Explore an intent-led content and discovery path for class-led local services.",
    },
  ],
  paid: [
    {
      href: "/local/restaurant-marketing-richmond-kingston-guildford/",
      title: "Restaurant paid-demand guide",
      description: "A local industry route for matching campaigns with time-sensitive restaurant demand.",
    },
    {
      href: "/local/gym-marketing-putney-wimbledon-kingston/",
      title: "Gym paid-demand guide",
      description: "Explore local campaign and enquiry considerations for fitness membership growth.",
    },
    {
      href: "/local/tyre-mot-garage-marketing-sw-london-surrey/",
      title: "Tyre, MOT and garage paid-demand guide",
      description: "A high-intent local search route for automotive service businesses.",
    },
  ],
  website: [
    {
      href: "/local/clinic-medical-dental-marketing-sw-london/",
      title: "Clinic and dental website guide",
      description: "See how local service pages can make treatments, locations and trust information easier to find.",
    },
    {
      href: "/local/salon-beauty-marketing-sw-london-surrey/",
      title: "Salon and beauty website guide",
      description: "A practical route for appointment-led businesses building clearer local service journeys.",
    },
    {
      href: "/local/law-firm-solicitor-marketing-sw-london-surrey/",
      title: "Law firm and solicitor website guide",
      description: "Explore how professional-services websites can clarify local expertise and next steps.",
    },
  ],
};

export default function LocalIndustryGuideLinks({
  context = "general",
  className = "",
}: {
  context?: LocalIndustryContext;
  className?: string;
}) {
  const guides = GUIDES[context];

  return (
    <section
      className={`mt-10 border border-[#00D4AA]/20 bg-[#00D4AA]/[0.035] p-5 sm:p-7 ${className}`.trim()}
      aria-labelledby={`local-industry-guides-${context}`}
    >
      <div className="flex items-start gap-3">
        <MapPin className="mt-1 h-5 w-5 shrink-0 text-[#00D4AA]" aria-hidden="true" />
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#00D4AA]">Local industry resources</p>
          <h2 id={`local-industry-guides-${context}`} className="mt-2 font-serif text-xl text-white sm:text-2xl">
            Explore a relevant local growth playbook
          </h2>
          <p className="mt-2 max-w-3xl text-sm leading-relaxed text-slate-300">
            These sector pages translate local discovery, content and measurement principles into a specific service-business context across SW London and Surrey.
          </p>
        </div>
      </div>

      <div className="mt-6 grid gap-3 md:grid-cols-3">
        {guides.map((guide) => (
          <Link
            key={guide.href}
            href={guide.href}
            onClick={() =>
              trackSectionCtaClick({
                section_name: `local_industry_guides_${context}`,
                cta_text: guide.title,
                destination: guide.href,
              })
            }
            className="group rounded-lg border border-white/10 bg-black/20 p-4 transition-colors hover:border-[#00D4AA]/50 hover:bg-white/[0.04]"
          >
            <h3 className="font-medium leading-snug text-white group-hover:text-teal-100">{guide.title}</h3>
            <p className="mt-2 text-xs leading-relaxed text-slate-300">{guide.description}</p>
            <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-[#00D4AA]">
              View sector page <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}
